import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Checkbox,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Alert,
    CircularProgress,
    FormControlLabel,
    IconButton,
    Collapse,
    Grid,
    Card,
    CardContent,
    LinearProgress,
    Tooltip
} from '@mui/material';
import {
    CheckBoxOutlineBlank,
    CheckBox,
    IndeterminateCheckBox,
    Visibility as PreviewIcon,
    Sync as SyncIcon,
    Warning as WarningIcon,
    CheckCircle as NewIcon,
    Update as ExistingIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    ViewList as GraphicViewIcon,
    Code as JsonViewIcon
} from '@mui/icons-material';

interface BookingPreview {
    id: string;
    code: string;
    person: {
        id: string;
        name: string;
        fiscalCode?: string;
    };
    startTime: string;
    endTime: string;
    service: {
        id: string;
        name: string;
        code: string;
    };
    diary?: {
        id: string;
        name: string;
    };
    executionUnit?: {
        id: string;
        name: string;
    };
    site?: {
        id: string;
        name: string;
    };
    status: {
        id: number;
        name: string;
    };
    localExists: boolean;
    localId?: string;
    localStatus: 'new' | 'existing';
    selected: boolean;
}

interface BookingPreviewSummary {
    total: number;
    existing: number;
    new: number;
    apiEndpoint: string;
    dateRange: {
        from: Date;
        to: Date;
    };
}

interface BookingPreviewDialogProps {
    open: boolean;
    onClose: () => void;
    onConfirm: (selectedBookings: string[], overwriteExisting: boolean) => void;
    syncOptions: any;
    loading?: boolean;
}

const BookingPreviewDialog: React.FC<BookingPreviewDialogProps> = ({
    open,
    onClose,
    onConfirm,
    syncOptions,
    loading = false
}) => {
    const [previewData, setPreviewData] = useState<{
        appointments: BookingPreview[];
        summary: BookingPreviewSummary;
    } | null>(null);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedBookings, setSelectedBookings] = useState<Set<string>>(new Set());
    const [overwriteExisting, setOverwriteExisting] = useState(false);
    const [expandedRow, setExpandedRow] = useState<string | null>(null);
    const [selectAll, setSelectAll] = useState(true);
    const [viewMode, setViewMode] = useState<'graphic' | 'json'>('graphic'); // Switch tra vista grafica e JSON

    // Carica l'anteprima quando si apre la modale
    useEffect(() => {
        if (open && !previewData) {
            fetchPreviewData();
        }
    }, [open, syncOptions]);

    const fetchPreviewData = async () => {
        try {
            setLoadingPreview(true);
            setError(null);

            const response = await fetch('http://localhost:3001/api/bookings/sync/preview', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify(syncOptions)
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            
            if (data.success) {
                setPreviewData(data.data);
                // Seleziona tutti di default
                const allIds = new Set(data.data.appointments.map((apt: BookingPreview) => apt.id));
                setSelectedBookings(allIds);
                setSelectAll(true);
            } else {
                throw new Error(data.error || 'Failed to fetch preview data');
            }
        } catch (err) {
            console.error('Error fetching preview:', err);
            setError(err instanceof Error ? err.message : 'Unknown error occurred');
        } finally {
            setLoadingPreview(false);
        }
    };

    const handleSelectAll = (checked: boolean) => {
        if (checked) {
            const allIds = new Set(previewData?.appointments.map(apt => apt.id) || []);
            setSelectedBookings(allIds);
        } else {
            setSelectedBookings(new Set());
        }
        setSelectAll(checked);
    };

    const handleSelectBooking = (bookingId: string, checked: boolean) => {
        const newSelected = new Set(selectedBookings);
        if (checked) {
            newSelected.add(bookingId);
        } else {
            newSelected.delete(bookingId);
        }
        setSelectedBookings(newSelected);
        
        // Aggiorna lo stato "select all"
        const totalBookings = previewData?.appointments.length || 0;
        setSelectAll(newSelected.size === totalBookings);
    };

    const handleConfirm = () => {
        const selectedIds = Array.from(selectedBookings);
        onConfirm(selectedIds, overwriteExisting);
    };

    const formatDateTime = (dateStr: string) => {
        try {
            const date = new Date(dateStr);
            return date.toLocaleString('it-IT', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return dateStr;
        }
    };

    const getStatusColor = (status: 'new' | 'existing') => {
        return status === 'new' ? 'success' : 'warning';
    };

    const getStatusIcon = (status: 'new' | 'existing') => {
        return status === 'new' ? <NewIcon /> : <ExistingIcon />;
    };

    const selectedCount = selectedBookings.size;
    const existingSelected = previewData?.appointments.filter(apt => 
        selectedBookings.has(apt.id) && apt.localExists
    ).length || 0;

    const getSelectAllState = () => {
        const total = previewData?.appointments.length || 0;
        if (selectedCount === 0) return false;
        if (selectedCount === total) return true;
        return 'indeterminate';
    };

    const renderSelectAllCheckbox = () => {
        const state = getSelectAllState();
        if (state === 'indeterminate') {
            return <IndeterminateCheckBox />;
        }
        return state ? <CheckBox /> : <CheckBoxOutlineBlank />;
    };

    return (
        <Dialog 
            open={open} 
            onClose={onClose} 
            maxWidth="xl" 
            fullWidth
            PaperProps={{ sx: { height: '90vh' } }}
        >
            <DialogTitle>
                <Box display="flex" alignItems="center" gap={1}>
                    <PreviewIcon />
                    <Typography variant="h6">
                        Anteprima Prenotazioni - {syncOptions?.apiEndpoint === 'orders' ? 'Orders' : 'Bookings'}
                    </Typography>
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                {loadingPreview ? (
                    <Box display="flex" flexDirection="column" alignItems="center" py={4}>
                        <CircularProgress size={40} />
                        <Typography variant="body2" color="textSecondary" mt={2}>
                            Recuperando prenotazioni da RECUP API...
                        </Typography>
                    </Box>
                ) : error ? (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        <Typography variant="h6">Errore nel recupero dati</Typography>
                        <Typography>{error}</Typography>
                        <Button onClick={fetchPreviewData} variant="outlined" size="small" sx={{ mt: 1 }}>
                            Riprova
                        </Button>
                    </Alert>
                ) : previewData ? (
                    <>
                        {/* Summary Cards */}
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            <Grid item xs={12} md={3}>
                                <Card>
                                    <CardContent>
                                        <Typography color="textSecondary" gutterBottom>
                                            Totale Prenotazioni
                                        </Typography>
                                        <Typography variant="h4">
                                            {previewData.summary.total}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <Card>
                                    <CardContent>
                                        <Typography color="textSecondary" gutterBottom>
                                            Nuove
                                        </Typography>
                                        <Typography variant="h4" color="success.main">
                                            {previewData.summary.new}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <Card>
                                    <CardContent>
                                        <Typography color="textSecondary" gutterBottom>
                                            Esistenti
                                        </Typography>
                                        <Typography variant="h4" color="warning.main">
                                            {previewData.summary.existing}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <Card>
                                    <CardContent>
                                        <Typography color="textSecondary" gutterBottom>
                                            Selezionate
                                        </Typography>
                                        <Typography variant="h4" color="primary.main">
                                            {selectedCount}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>

                        {/* Opzioni */}
                        <Box sx={{ mb: 2 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={overwriteExisting}
                                        onChange={(e) => setOverwriteExisting(e.target.checked)}
                                        color="warning"
                                    />
                                }
                                label="Sovrascrivi prenotazioni esistenti"
                            />
                            <Typography variant="body2" color="textSecondary" sx={{ ml: 4 }}>
                                Se selezionato, le prenotazioni già esistenti verranno aggiornate con i nuovi dati
                            </Typography>
                        </Box>

                        {/* Tabella Prenotazioni */}
                        <TableContainer component={Paper}>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                icon={renderSelectAllCheckbox()}
                                                checkedIcon={<CheckBox />}
                                                checked={getSelectAllState() === true}
                                                indeterminate={getSelectAllState() === 'indeterminate'}
                                                onChange={(e) => handleSelectAll(e.target.checked)}
                                            />
                                        </TableCell>
                                        <TableCell>Stato</TableCell>
                                        <TableCell>ID</TableCell>
                                        <TableCell>Paziente</TableCell>
                                        <TableCell>Servizio</TableCell>
                                        <TableCell>Data/Ora</TableCell>
                                        <TableCell>Unità Operativa</TableCell>
                                        <TableCell>Azioni</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {previewData.appointments.map((appointment) => (
                                        <React.Fragment key={appointment.id}>
                                            <TableRow 
                                                hover 
                                                sx={{ 
                                                    backgroundColor: selectedBookings.has(appointment.id) ? 'action.selected' : 'inherit'
                                                }}
                                            >
                                                <TableCell padding="checkbox">
                                                    <Checkbox
                                                        checked={selectedBookings.has(appointment.id)}
                                                        onChange={(e) => handleSelectBooking(appointment.id, e.target.checked)}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        icon={getStatusIcon(appointment.localStatus)}
                                                        label={appointment.localStatus === 'new' ? 'Nuova' : 'Esistente'}
                                                        color={getStatusColor(appointment.localStatus)}
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" fontWeight="bold">
                                                        {appointment.id}
                                                    </Typography>
                                                    <Typography variant="caption" color="textSecondary">
                                                        {appointment.code}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {appointment.person.name}
                                                    </Typography>
                                                    {appointment.person.fiscalCode && (
                                                        <Typography variant="caption" color="textSecondary">
                                                            CF: {appointment.person.fiscalCode}
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {appointment.service.name}
                                                    </Typography>
                                                    <Typography variant="caption" color="textSecondary">
                                                        {appointment.service.code}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" fontWeight="bold">
                                                        {formatDateTime(appointment.startTime)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {appointment.executionUnit?.name || 'N/A'}
                                                    </Typography>
                                                    {appointment.diary?.name && (
                                                        <Typography variant="caption" color="textSecondary">
                                                            {appointment.diary.name}
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <Tooltip title="Mostra dettagli">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => setExpandedRow(
                                                                expandedRow === appointment.id ? null : appointment.id
                                                            )}
                                                        >
                                                            {expandedRow === appointment.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell colSpan={8} sx={{ p: 0 }}>
                                                    <Collapse in={expandedRow === appointment.id}>
                                                        <Box sx={{ p: 2, backgroundColor: 'grey.50' }}>
                                                            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                                                                <Typography variant="subtitle2">
                                                                    Dettagli Prenotazione
                                                                </Typography>
                                                                <Box>
                                                                    <Tooltip title="Vista Grafica">
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() => setViewMode('graphic')}
                                                                            color={viewMode === 'graphic' ? 'primary' : 'default'}
                                                                        >
                                                                            <GraphicViewIcon />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                    <Tooltip title="Vista JSON">
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() => setViewMode('json')}
                                                                            color={viewMode === 'json' ? 'primary' : 'default'}
                                                                        >
                                                                            <JsonViewIcon />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </Box>
                                                            </Box>

                                                            {viewMode === 'graphic' ? (
                                                                /* Vista Grafica Strutturata */
                                                                <Grid container spacing={2}>
                                                                    {/* Sezione Informazioni Base */}
                                                                    <Grid item xs={12}>
                                                                        <Typography variant="subtitle2" color="primary" gutterBottom>
                                                                            📋 Informazioni Base
                                                                        </Typography>
                                                                        <Grid container spacing={1}>
                                                                            <Grid item xs={6}>
                                                                                <Typography variant="body2">
                                                                                    <strong>ID RECUP:</strong> {appointment.id || 'N/A'}
                                                                                </Typography>
                                                                                <Typography variant="body2">
                                                                                    <strong>Codice:</strong> {appointment.code || 'N/A'}
                                                                                </Typography>
                                                                                <Typography variant="body2">
                                                                                    <strong>Stato:</strong> {appointment.status?.name || 'N/A'} (ID: {appointment.status?.id || 'N/A'})
                                                                                </Typography>
                                                                            </Grid>
                                                                            <Grid item xs={6}>
                                                                                <Typography variant="body2">
                                                                                    <strong>Data Inizio:</strong> {appointment.startTime ? formatDateTime(appointment.startTime) : 'N/A'}
                                                                                </Typography>
                                                                                <Typography variant="body2">
                                                                                    <strong>Data Fine:</strong> {appointment.endTime ? formatDateTime(appointment.endTime) : 'N/A'}
                                                                                </Typography>
                                                                                {appointment.localExists && (
                                                                                    <Typography variant="body2" color="warning.main">
                                                                                        <strong>ID Locale:</strong> {appointment.localId || 'N/A'}
                                                                                    </Typography>
                                                                                )}
                                                                            </Grid>
                                                                        </Grid>
                                                                    </Grid>

                                                                    {/* Sezione Paziente */}
                                                                    <Grid item xs={12}>
                                                                        <Typography variant="subtitle2" color="primary" gutterBottom>
                                                                            👤 Informazioni Paziente
                                                                        </Typography>
                                                                        <Grid container spacing={1}>
                                                                            <Grid item xs={6}>
                                                                                <Typography variant="body2">
                                                                                    <strong>ID Paziente:</strong> {appointment.person?.id || 'N/A'}
                                                                                </Typography>
                                                                                <Typography variant="body2">
                                                                                    <strong>Nome:</strong> {appointment.person?.name || 'N/A'}
                                                                                </Typography>
                                                                            </Grid>
                                                                            <Grid item xs={6}>
                                                                                <Typography variant="body2">
                                                                                    <strong>Codice Fiscale:</strong> {appointment.person?.fiscalCode || 'N/A'}
                                                                                </Typography>
                                                                            </Grid>
                                                                        </Grid>
                                                                    </Grid>

                                                                    {/* Sezione Servizio */}
                                                                    <Grid item xs={12}>
                                                                        <Typography variant="subtitle2" color="primary" gutterBottom>
                                                                            🏥 Informazioni Servizio
                                                                        </Typography>
                                                                        <Grid container spacing={1}>
                                                                            <Grid item xs={6}>
                                                                                <Typography variant="body2">
                                                                                    <strong>ID Servizio:</strong> {appointment.service?.id || 'N/A'}
                                                                                </Typography>
                                                                                <Typography variant="body2">
                                                                                    <strong>Nome Servizio:</strong> {appointment.service?.name || 'N/A'}
                                                                                </Typography>
                                                                            </Grid>
                                                                            <Grid item xs={6}>
                                                                                <Typography variant="body2">
                                                                                    <strong>Codice Servizio:</strong> {appointment.service?.code || 'N/A'}
                                                                                </Typography>
                                                                            </Grid>
                                                                        </Grid>
                                                                    </Grid>

                                                                    {/* Sezione Struttura */}
                                                                    <Grid item xs={12}>
                                                                        <Typography variant="subtitle2" color="primary" gutterBottom>
                                                                            🏢 Informazioni Struttura
                                                                        </Typography>
                                                                        <Grid container spacing={1}>
                                                                            <Grid item xs={6}>
                                                                                <Typography variant="body2">
                                                                                    <strong>ID Agenda:</strong> {appointment.diary?.id || 'N/A'}
                                                                                </Typography>
                                                                                <Typography variant="body2">
                                                                                    <strong>Nome Agenda:</strong> {appointment.diary?.name || 'N/A'}
                                                                                </Typography>
                                                                                <Typography variant="body2">
                                                                                    <strong>ID Unità Operativa:</strong> {appointment.executionUnit?.id || 'N/A'}
                                                                                </Typography>
                                                                            </Grid>
                                                                            <Grid item xs={6}>
                                                                                <Typography variant="body2">
                                                                                    <strong>Nome Unità Operativa:</strong> {appointment.executionUnit?.name || 'N/A'}
                                                                                </Typography>
                                                                                <Typography variant="body2">
                                                                                    <strong>ID Sito:</strong> {appointment.site?.id || 'N/A'}
                                                                                </Typography>
                                                                                <Typography variant="body2">
                                                                                    <strong>Nome Sito:</strong> {appointment.site?.name || 'N/A'}
                                                                                </Typography>
                                                                            </Grid>
                                                                        </Grid>
                                                                    </Grid>
                                                                </Grid>
                                                            ) : (
                                                                /* Vista JSON */
                                                                <Box>
                                                                    <Typography variant="subtitle2" gutterBottom>
                                                                        Dati Completi JSON (da RECUP API):
                                                                    </Typography>
                                                                    <Box sx={{ 
                                                                        backgroundColor: 'grey.100', 
                                                                        p: 1, 
                                                                        borderRadius: 1, 
                                                                        maxHeight: '400px', 
                                                                        overflow: 'auto',
                                                                        fontSize: '0.8rem',
                                                                        fontFamily: 'monospace'
                                                                    }}>
                                                                        <pre>{JSON.stringify(appointment, null, 2)}</pre>
                                                                    </Box>
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    </Collapse>
                                                </TableCell>
                                            </TableRow>
                                        </React.Fragment>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {existingSelected > 0 && !overwriteExisting && (
                            <Alert severity="warning" sx={{ mt: 2 }}>
                                <Typography variant="body2">
                                    Hai selezionato {existingSelected} prenotazione/i già esistente/i. 
                                    Senza l'opzione "Sovrascrivi", queste verranno saltate durante la sincronizzazione.
                                </Typography>
                            </Alert>
                        )}
                    </>
                ) : (
                    <Typography>Nessun dato disponibile</Typography>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} color="inherit">
                    Annulla
                </Button>
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    startIcon={<SyncIcon />}
                    disabled={loadingPreview || selectedCount === 0 || loading}
                    color="primary"
                >
                    {loading ? (
                        <>
                            <CircularProgress size={20} sx={{ mr: 1 }} />
                            Sincronizzando...
                        </>
                    ) : (
                        `Sincronizza ${selectedCount} Prenotazione/i`
                    )}
                </Button>
            </DialogActions>

            {loading && (
                <LinearProgress />
            )}
        </Dialog>
    );
};

export default BookingPreviewDialog;